import { useId, useState } from "react"
import clsx from "clsx"
import { Form, Formik } from "formik"
import { Modal } from "react-bootstrap"
import * as Yup from "yup"
import S from "./rating-modal.module.scss"
import RatingSubmitted from "../../svgs/rating-submitted.svg"
import FormikTextarea from "@/components/formik-textarea"
import RatingField from "@/components/rating-field"
import API from "@/lib/api"
import { event } from "@/lib/tracking"
import useUser from "@/lib/useUser"
import { Product } from "@/types"

const RatingModal = ({
  open,
  product,
  onClose,
}: {
  open: boolean
  product: Product
  onClose: () => void
}) => {
  const { user } = useUser()
  const [submitted, setSubmitted] = useState(false)
  const [serverError, setServerError] = useState()
  const id = useId()

  return (
    <Formik
      initialValues={{
        rating: 0,
        comment: "",
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setServerError(undefined)

        try {
          await API.services.products.rate(user!.access_token, product.slug, {
            rating: values.rating,
            comment: values.comment,
          })
          setSubmitted(true)
          event({
            category: "ecommerce",
            name: "product",
            action: "rated",
            customFields: {
              product,
            },
          })
        } catch (error: any) {
          setServerError(error?.message)
        } finally {
          setSubmitting(false)
        }
      }}
      validationSchema={Yup.object().shape({
        rating: Yup.number()
          .min(1, "Rating must be at least 1")
          .max(5, "Rating must be at most 5")
          .required("Rating is required"),
        comment: Yup.string().required("Feedback is required"),
      })}
    >
      {({ isSubmitting }) => (
        <Form id={id}>
          <Modal show={open} onHide={onClose} centered>
            {submitted ? (
              <>
                <Modal.Header closeButton>&nbsp;</Modal.Header>
                <Modal.Body>
                  <div className={clsx("text-center", S.submitted)}>
                    <RatingSubmitted />
                    <p className="u-section-title">
                      Thank you for your feedback.
                    </p>
                  </div>
                </Modal.Body>
              </>
            ) : (
              <>
                <Modal.Header closeButton>
                  <Modal.Title>Feedback about this product</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {serverError && (
                    <div className="alert alert-danger">{serverError}</div>
                  )}

                  <div className="form-group">
                    <RatingField label="Rate the Product" name="rating" />
                  </div>

                  <div className="form-group">
                    <FormikTextarea
                      label="General Feedback"
                      placeholder="Start typing here..."
                      name="comment"
                      rows={5}
                    />
                  </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                  <button
                    className="btn btn-outline-dark"
                    type="button"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    form={id}
                    type="submit"
                    className={clsx("btn btn-primary", {
                      loader: isSubmitting,
                    })}
                    disabled={isSubmitting}
                  >
                    Submit
                  </button>
                </Modal.Footer>
              </>
            )}
          </Modal>
        </Form>
      )}
    </Formik>
  )
}

export default RatingModal
